/**
 * Pure hulpjes voor het scriptpaneel (scripttool uit SharePoint, 3 nov 2026). Identiek op develop en main.
 */

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** '2026-11-01T10:00:00Z' → '1 nov' (met jaartal als het niet dit jaar is). */
export function korteDatum(iso, nu = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const basis = `${d.getDate()} ${MAANDEN[d.getMonth()]}`;
  return d.getFullYear() === nu.getFullYear() ? basis : `${basis} ${d.getFullYear()}`;
}

/** 'v3 · gewijzigd 1 nov door Bram' */
export function versieZin(versie, nu) {
  if (!versie) return '';
  const delen = [versie.versie && versie.versie !== 'zonder versie' ? versie.versie : null];
  const datum = korteDatum(versie.gewijzigdOp, nu);
  if (datum) delen.push(`gewijzigd ${datum}${versie.gewijzigdDoor ? ` door ${versie.gewijzigdDoor}` : ''}`);
  return delen.filter(Boolean).join(' · ');
}

/** Word-app-link (ms-word-protocol) voor een SharePoint-bestand; alleen https. */
export function wordAppUrl(webUrl) {
  if (!webUrl || !/^https:\/\//i.test(webUrl)) return null;
  return `ms-word:ofe|u|${webUrl}`;
}

export const mijlpaalLabel = (soort) => (soort === 'def' ? 'DEF' : soort);

/** Mijlpalen per bestand-item: { itemId: ['v1', 'DEF'] }. */
export function mijlpalenPerItem(mijlpalen = []) {
  const uit = {};
  mijlpalen.forEach((m) => {
    if (!m?.item) return;
    (uit[m.item] = uit[m.item] || []).push(mijlpaalLabel(m.soort));
  });
  return uit;
}

/** De actieve DEF van een reeks, of null. */
export const defVan = (mijlpalen = [], reeksSleutel) =>
  mijlpalen.find((m) => m.reeks === reeksSleutel && m.soort === 'def') || null;

/**
 * Is het script na de DEF nog veranderd? Ja als er een nieuwere versie in de reeks staat dan het DEF-bestand,
 * of als het DEF-bestand zelf een andere etag heeft dan bij het vastleggen. Is de actuele tekst van dat bestand
 * geladen (`tekst`, uit /tekst), dan telt die etag: die is verser dan de lijst.
 */
export function gewijzigdNaDef(def, reeks, tekst = null) {
  if (!def || !reeks?.versies?.length) return false;
  const laatste = reeks.versies[0];
  if (laatste.item !== def.item) return true;
  const actueel = tekst?.item === laatste.item && tekst?.etag ? tekst.etag : laatste.etag;
  return !!(def.etag && actueel && def.etag !== actueel);
}

/**
 * Lange ongewijzigde stukken inklappen: `context` regels rond elk verschil blijven staan, de rest wordt
 * { soort: 'ingeklapt', aantal, regels }. Begin en eind van het document tellen als één kant.
 */
export function klapGelijkIn(stukken = [], context = 2) {
  const uit = [];
  stukken.forEach((s, i) => {
    if (s.soort !== 'gelijk') { uit.push(s); return; }
    const regels = s.regels || [];
    const ervoor = i > 0;
    const erna = i < stukken.length - 1;
    const kop = ervoor ? context : 0;
    const staart = erna ? context : 0;
    if (regels.length <= kop + staart + 1) { uit.push(s); return; }
    if (kop) uit.push({ soort: 'gelijk', regels: regels.slice(0, kop) });
    uit.push({ soort: 'ingeklapt', aantal: regels.length - kop - staart, regels: regels.slice(kop, regels.length - staart) });
    if (staart) uit.push({ soort: 'gelijk', regels: regels.slice(regels.length - staart) });
  });
  return uit;
}

/** Korte samenvatting van een verschil: '3 regels erbij, 1 weg, 2 gewijzigd'. */
export function verschilZin(telling, afgekapt = false) {
  if (!telling) return '';
  const delen = [];
  if (telling.erbij) delen.push(`${telling.erbij} ${telling.erbij === 1 ? 'regel' : 'regels'} erbij`);
  if (telling.weg) delen.push(`${telling.weg} weg`);
  if (telling.gewijzigd) delen.push(`${telling.gewijzigd} gewijzigd`);
  if (delen.length) return delen.join(', ') + (afgekapt ? ' in het vergeleken deel' : '');
  return afgekapt ? 'geen verschil in het vergeleken deel' : 'geen verschil in de tekst';
}

/** Leesbare foutmelding uit een API-antwoord. */
export async function foutUit(r, standaard = 'Dat lukte niet') {
  try {
    const d = await r.json();
    return d?.detail || d?.error || standaard;
  } catch {
    return standaard;
  }
}
